import FETCHRAW from "@/api/fetchRaw";
import { backendPublicPath } from "@/api/servicePaths";
import { SHOW_URL } from "@/configs";

export type ProductRouteKey =
  | "llmgw/landing"
  | "llmgw_admin/admin"
  | "edge_helpdesk/landing"
  | "edge_helpdesk/admin"
  | "show/admin";

type ProductHandoffResponse = {
  open_url?: string;
  launch_url?: string;
  content?: ProductHandoffResponse;
  handoff?: ProductHandoffResponse;
};

export const PRODUCT_PATHS = {
  "llmgw/landing": "/llmgw",
  "llmgw_admin/admin": "/llmgw/admin",
  "edge_helpdesk/landing": "/edge/helpdesk",
  "edge_helpdesk/admin": "/edge/helpdesk/admin",
  "show/admin": "/show/admin"
} as const satisfies Record<ProductRouteKey, string>;

/** The LLMGW landing is rendered by the Worksine UI and must not consume a handoff code. */
export const UI_NATIVE_PRODUCT_ROUTE_KEYS = ["llmgw/landing"] as const satisfies readonly ProductRouteKey[];

export function isUiNativeProductRoute(routeKey: ProductRouteKey): boolean {
  return (UI_NATIVE_PRODUCT_ROUTE_KEYS as readonly string[]).includes(routeKey);
}

const PRODUCT_HANDOFF_PATH = backendPublicPath("/account/application-handoffs/");

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function configuredShowUrl(): URL {
  if (!isBrowser()) throw new Error("Show launch requires a browser session");
  return new URL(SHOW_URL || PRODUCT_PATHS["show/admin"], window.location.origin);
}

export async function buildShowLaunchUrl(): Promise<string> {
  const target = configuredShowUrl();
  const returnPath = `${target.pathname.replace(/\/$/, "") || "/"}${target.search}`;

  const response = await FETCHRAW({
    type: "apiurl",
    path: backendPublicPath("/show/session-handoff/"),
    method: "POST",
    headers: { "st-auth-mode": "header", rid: "session" },
    body: { returnPath },
    withToken: true,
    suppressAuthLogoutOn401: true,
    suppressNoTenantLogoutAndRedirect: true
  });
  const token = (response.content as { token?: string } | undefined)?.token;
  if (!token) throw new Error("The Show session handoff was not created");

  const loginPath = target.pathname.replace(/\/admin\/?$/, "") + "/login/handoff";
  const handoff = new URL(loginPath, target.origin);
  handoff.searchParams.set("token", token);
  return handoff.toString();
}

function readLaunchUrl(payload: unknown): string {
  if (!payload || typeof payload !== "object") return "";
  const response = payload as ProductHandoffResponse;
  const directUrl = String(response.open_url || response.launch_url || "").trim();
  if (directUrl) return directUrl;

  for (const nested of [response.content, response.handoff]) {
    if (!nested || typeof nested !== "object") continue;
    const nestedResponse = nested as ProductHandoffResponse;
    const nestedUrl = String(nestedResponse.open_url || nestedResponse.launch_url || "").trim();
    if (nestedUrl) return nestedUrl;
  }
  return "";
}

function normalizeProductLaunchUrl(value: string, routeKey: ProductRouteKey): string {
  if (!isBrowser()) throw new Error("Product launch requires a browser session");
  const parsed = new URL(value, window.location.origin);
  const expectedPath = PRODUCT_PATHS[routeKey];
  const isExpectedPath = parsed.pathname === expectedPath || parsed.pathname.startsWith(`${expectedPath}/`);
  if (parsed.origin !== window.location.origin || !isExpectedPath) {
    throw new Error("Product handoff returned an unexpected route");
  }
  const forbiddenQueryKeys = new Set([
    "tenant",
    "tenant_id",
    "workspace",
    "workspace_id",
    "project",
    "project_id",
    "organization",
    "organization_id",
    "credential",
    "redirect",
    "redirect_uri",
    "return_url"
  ]);
  if (Array.from(parsed.searchParams.keys()).some((key) => forbiddenQueryKeys.has(key.toLowerCase()))) {
    throw new Error("Product handoff returned a tenant-selecting URL");
  }
  return parsed.toString();
}

export async function createProductHandoff(routeKey: ProductRouteKey, purpose = "customer_product_launch") {
  if (isUiNativeProductRoute(routeKey)) {
    throw new Error("This product landing is served by the Worksine UI");
  }
  const [destination, targetRoute] = routeKey.split("/");
  const response = await FETCHRAW({
    type: "apiurl",
    path: PRODUCT_HANDOFF_PATH,
    method: "POST",
    body: JSON.stringify({
      destination: destination === "llmgw_admin" ? "llmgw_admin" : destination,
      purpose,
      route_key: targetRoute
    }),
    suppressAuthLogoutOn401: true,
    suppressNoTenantLogoutAndRedirect: true
  });
  const launchUrl = readLaunchUrl(response.content);
  if (!launchUrl) throw new Error("The product handoff did not return a launch URL");
  return normalizeProductLaunchUrl(launchUrl, routeKey);
}

export async function launchProduct(routeKey: ProductRouteKey): Promise<void> {
  if (!isBrowser()) return;
  if (routeKey === "show/admin") {
    window.location.assign(await buildShowLaunchUrl());
    return;
  }
  if (isUiNativeProductRoute(routeKey)) {
    window.location.assign(PRODUCT_PATHS[routeKey]);
    return;
  }
  const launchUrl = await createProductHandoff(routeKey);
  window.location.assign(launchUrl);
}
